import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { API_BASE_URL, DEFAULT_LANGUAGE } from '../../utils/constants';

// Helper function to get current language
const getCurrentLanguage = (): string => {
  const stored = localStorage.getItem('i18nextLng');
  return stored || DEFAULT_LANGUAGE;
};

// Base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    // Bypass ngrok browser warning page
    headers.set('ngrok-skip-browser-warning', 'true');
    
    // Add Accept-Language header for API responses
    const language = getCurrentLanguage();
    headers.set('Accept-Language', language);
    
    return headers;
  },
});

// Base query with re-authentication on 401
const baseQueryWithReauth = async (args: unknown, api: unknown, extraOptions: unknown) => {
  const result = await baseQuery(args as Parameters<typeof baseQuery>[0], api as Parameters<typeof baseQuery>[1], extraOptions as Parameters<typeof baseQuery>[2]);

  // Handle 401 Unauthorized - redirect to login
  // BUT: Don't redirect for public auth endpoints (login, register, etc.)
  if (result.error && result.error.status === 401) {
    const url = typeof args === 'string' ? args : (args as { url?: string })?.url || '';
    
    // List of public endpoints that should NOT trigger redirect on 401
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/telegram-login',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/resend-verification',
      '/auth/verify-login-otp',
      '/auth/resend-login-otp',
      '/auth/toggle-2fa',
      '/auth/confirm-enable-2fa',
      '/exchange-rate',
      '/maintenance/status',
    ];
    
    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some((endpoint) => 
      url.includes(endpoint)
    );
    
    // Only redirect if it's NOT a public endpoint
    if (!isPublicEndpoint) {
      // Clear user data
      localStorage.removeItem('user');
      
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  // Handle other errors
  if (result.error) {
    // Log error for debugging (in development)
    if (import.meta.env.DEV) {
      console.error('API Error:', result.error);
    }
    
    // TODO: Send to error tracking service (Sentry)
  }

  return result;
};

// Create base API
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Users',
    'Product',
    'Products',
    'Category',
    'Categories',
    'Transaction',
    'Transactions',
    'Balance',
    'Purchases',
    'SystemConfig',
    'ExternalApiKeys',
    'CashbackSettings',
    'PaymentGateways',
    'TemporaryNumbers',
    'TemporaryNumber',
    'PermanentNumbers',
    'PermanentNumber',
    'Proxies',
    'Proxy',
    'UserMargins',
    'MarginTiers',
    'Maintenance',
    'DisabledServers',
    'Voice',
    'SystemWebhookSecret',
    'HideProducts',
    'Tickets',
    'Ticket',
    'TicketMessages',
    'AllTickets',
    'AdminTicket',
    'Tags',
    'DashboardStats',
    'Proxy5gDetails',
  ],
  endpoints: () => ({}),
});

